import { t } from "@lingui/macro";
import cx from "classnames";
import { useState } from "react";

import CheckIcon from "assets/icons/CheckIcon";
import { dynamicActivate, getAutomaticLocale, getManualLocale, locales, resetToAutomaticLocale } from "lib/i18n";

type Props = {
  onClose: () => void;
};

export const getLanguageName = (langCode: string) => {
  const displayNames = new Intl.DisplayNames([langCode], { type: "language" });
  return displayNames.of(langCode);
};

export default function LanguageModalContent({ onClose }: Props) {
  const [isActivating, setIsActivating] = useState(false);
  const [activationError, setActivationError] = useState<string | undefined>();
  const automaticLocale = getAutomaticLocale();
  const manualLocale = getManualLocale();
  const isAutomaticSelected = !manualLocale;

  async function activate(action: () => Promise<unknown>) {
    setIsActivating(true);
    setActivationError(undefined);

    try {
      await action();
      onClose();
    } catch (_error) {
      setActivationError(t`Could not change language. Please try again.`);
    } finally {
      setIsActivating(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className={cx("network-dropdown-menu-item menu-item language-modal-item language-modal-item-automatic", {
          active: isAutomaticSelected,
        })}
        onClick={() => activate(resetToAutomaticLocale)}
        disabled={isActivating}
        aria-pressed={isAutomaticSelected}
        aria-label={t`Automatic language (${getLanguageName(automaticLocale)})`}
      >
        <span className="language-item">
          {t`Automatic`}
          <br />
          <span className="language-item__note">({getLanguageName(automaticLocale)})</span>
        </span>
      </button>
      {Object.keys(locales).map((item) => {
        const isActive = manualLocale === item;
        return (
          <button
            type="button"
            key={item}
            className={cx("network-dropdown-menu-item  menu-item language-modal-item", {
              active: isActive,
            })}
            onClick={() => {
              void activate(() => dynamicActivate(item));
            }}
            disabled={isActivating}
            aria-pressed={isActive}
          >
            <div className="menu-item-group">
              <p className="language-item">
                {getLanguageName(item)}
                <br />
                <span className="language-item__note">({locales[item]})</span>
              </p>
            </div>
            <div className="network-dropdown-menu-item-img">
              {isActive && <CheckIcon className="language-checked-icon" />}
            </div>
          </button>
        );
      })}
      {activationError && (
        <p className="language-modal-error" role="alert">
          {activationError}
        </p>
      )}
    </>
  );
}
